import { useState } from 'react';
import { ButtonLink, GridAlpha, useToasts } from '@applyboard/crystal-ui';
import { useCurrentUser, useEventTracker } from '@applyboard/ui-utils';
import { useGetReferringRPId } from '../../common/hooks/useQueryParams';
import { appendQueryParameters } from '../../common/utils/appendQueryParameters';
import { FxRates, useGetLatestFxRates } from '../../data/fxRates';
import { VAS_HUB_TRACKING_SOURCE } from '../../pages/VasHub/constants';
import LoanEligibilityCheckerDialog from '../LoanEligibilityChecker/LoanEligibilityCheckerDialog';
import { RequestInformationDialog } from '../RequestInformation';
import { VasSpinner } from '../Spinner';
import { CardProps, default as ServiceCard, ServiceCardFooter, ServiceCardSubMenu } from '.';

export const StudentLoanServiceCard = ({
  cardKey,
  serviceLabel,
  badge = '',
  image,
  isImageForAvatar = false,
  title,
  isTitleUnderImage = false,
  description,
  shareLinkButtonName,
  applyButtonCTAUrl,
  applyButtonName,
  vendorUrl,
  disabled = false,
  tooltip,
}: CardProps) => {
  const { trackEvent } = useEventTracker();
  const [currentUser] = useCurrentUser();
  const toasts = useToasts();
  const rpId =
    useGetReferringRPId() ||
    (currentUser?.relationships as { agent?: { data: { id: string } } })?.agent?.data?.id ||
    '';
  const [requestInfoOpen, setRequestInfoOpen] = useState(false);
  const { fxRates, isLoadingFxRates, fxRatesError } = useGetLatestFxRates();

  if (isLoadingFxRates) {
    return <VasSpinner />;
  }

  if (fxRatesError) {
    toasts.negative(
      new Error('Unable to check loan eligibility at this time. Please try again later.'),
    );
    return <></>;
  }

  return (
    <>
      <ServiceCard
        cardKey={cardKey}
        serviceLabel={serviceLabel}
        badge={badge}
        image={image}
        isImageForAvatar={isImageForAvatar}
        title={title}
        isTitleUnderImage={isTitleUnderImage}
        description={description}
        shareLinkButtonName={shareLinkButtonName}
        applyButtonCTAUrl={applyButtonCTAUrl}
        applyButtonName={applyButtonName}
        vendorUrl={vendorUrl}
        disabled={disabled}
        tooltip={tooltip}
      >
        <ServiceCardSubMenu
          id="requestInfo"
          key="requestInfo"
          onClick={() => {
            setRequestInfoOpen(true);
            trackEvent({
              event: 'VAS - Vas Hub Page - Service Card - Request Information button clicked',
              data: {
                source: 'RP Service Card',
                rp_id: rpId,
                service: 'loans',
                vendor: 'student',
              },
            });
          }}
        >
          Request Info
        </ServiceCardSubMenu>
        <ServiceCardFooter>
          <GridAlpha columns="repeat(12, 1fr)" columnGap={4} rowGap={4}>
            <GridAlpha.Item
              columnStart={{
                xs: 1,
                sm: 1,
              }}
              columnEnd={{
                xs: 13,
                sm: 7,
              }}
              rowStart={{ xs: 2, sm: 1 }}
            >
              <LoanEligibilityCheckerDialog
                fxRates={fxRates ?? ({} as FxRates)}
                disabled={disabled}
              />
            </GridAlpha.Item>
            <GridAlpha.Item
              columnStart={{
                xs: 1,
                sm: 7,
              }}
              columnEnd={{
                xs: 13,
                sm: 13,
              }}
              rowStart={{ xs: 1, sm: 1 }}
            >
              <ButtonLink
                intent="primary"
                size="md"
                emphasis="highlighted"
                width="fill"
                href={
                  applyButtonCTAUrl || vendorUrl
                    ? appendQueryParameters(applyButtonCTAUrl || vendorUrl || '', {
                        rp_id: rpId,
                        source: VAS_HUB_TRACKING_SOURCE,
                      })
                    : ''
                }
                mode="override_user_choice_and_open_in_new_tab"
                onClick={() => {
                  trackEvent({
                    event: `VAS - Vas Hub Page - Card Apply Button Clicked`,
                    data: { rp_id: rpId, source: VAS_HUB_TRACKING_SOURCE, card: cardKey },
                  });
                }}
              >
                {applyButtonName}
              </ButtonLink>
            </GridAlpha.Item>
          </GridAlpha>
        </ServiceCardFooter>
      </ServiceCard>
      <RequestInformationDialog
        name={currentUser?.full_name ?? ''}
        email={currentUser?.email ?? ''}
        rpId={rpId}
        service="Student"
        vendor="Loan"
        externalDialogControl={{
          open: requestInfoOpen,
          setOpen: setRequestInfoOpen,
        }}
      />
    </>
  );
};

export default StudentLoanServiceCard;
